import React, { useState } from 'react';
import { Alert, Box, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';
import { Field, FormikProvider, useFormik } from 'formik';
import { useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import { requestPasswordReset } from '../../../services/passwordReset';
import colorConfigs from '../../../configs/colorConfigs';
import AuthPageFrame from '../AuthPageFrame';
import RareButton from '../buttons/RareButton';
import { FormEmailTextField } from '../fields/AuthFields';

export default function ForgottenPasswordCard() {
  const [showDialog, setShowDialog] = useState(false);
  const [requestError, setRequestError] = useState(false);
  const navigate = useNavigate();

  const formik = useFormik({
    initialValues: { email: '' },
    validationSchema: Yup.object({
      email: Yup.string().trim().email('Invalid email format').required('This field is required'),
    }),
    onSubmit: async (values) => {
      setRequestError(false);
      try {
        await requestPasswordReset(values.email);
        setShowDialog(true);
      } catch {
        setRequestError(true);
      }
    },
  });

  const returnToLogin = () => navigate('/login');

  return (
    <AuthPageFrame title="Forgot Password">
      <FormikProvider value={formik}>
        <Box component="form" onSubmit={formik.handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {requestError && <Alert severity="error">The recovery request could not be sent. Please try again.</Alert>}
          <Field name="email" type="email" label="Email" autoComplete="email" autoFocus component={FormEmailTextField} />
          <Box sx={{ mt: 1, pt: 2, borderTop: '1px solid #e1e8e6', display: 'flex', gap: 1.5, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
            <RareButton variant="outlined" onClick={returnToLogin} sx={{ mr: { sm: 'auto' }, flex: { xs: '1 1 100%', sm: '0 0 auto' } }}>
              Return to Login
            </RareButton>
            <RareButton variant="contained" type="submit" disabled={formik.isSubmitting} sx={{ flex: { xs: '1 1 100%', sm: '0 0 auto' } }}>
              Submit
            </RareButton>
          </Box>
        </Box>
      </FormikProvider>
      <Dialog
        open={showDialog}
        onClose={returnToLogin}
        fullWidth
        maxWidth="sm"
        aria-labelledby="password-recovery-title"
        aria-describedby="password-recovery-description"
        PaperProps={{ sx: { borderRadius: 2, m: 2, width: 'calc(100% - 32px)' } }}
      >
        <DialogTitle id="password-recovery-title" sx={{ textAlign: 'center', color: colorConfigs.tables.headBg, fontSize: 24, fontWeight: 700, py: 2 }}>
          Password Recovery
        </DialogTitle>
        <DialogContent sx={{ px: { xs: 2, sm: 3 }, py: 2 }}>
          <DialogContentText id="password-recovery-description">
            If we can process your request for <strong>{formik.values.email}</strong>, you’ll receive password-reset instructions. Check your inbox and spam folder. Repeated requests are limited; try again later or contact support.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ borderTop: '1px solid #e1e8e6', px: { xs: 2, sm: 3 }, py: 2 }}>
          <RareButton variant="outlined" onClick={returnToLogin} sx={{ width: { xs: '100%', sm: 'auto' } }}>
            Return to Login
          </RareButton>
        </DialogActions>
      </Dialog>
    </AuthPageFrame>
  );
}
